// components/middleware/UserAuth.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { Spin } from "antd";
import toast from "react-hot-toast";
import Layout from "../Layout";
import { LOGIN_ROUTE } from "../../routes/constant";
import { getUserProfile } from "../../store/slices/profileSlice";
import { isAuthenticated } from "../../utils/authUtils";

// UserAuth Middleware Component
const UserAuth = ({
  children,
  requiredRole = null,
  requiredVerification = false,
  redirectTo = LOGIN_ROUTE,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  // Get auth state from auth slice
  const { isLoggedIn, loading: authLoading } = useSelector(
    (state) => state.auth,
  );

  // Get user data from profile slice
  const {
    user,
    loading: profileLoading,
    error,
  } = useSelector((state) => state.profile);

  const loading = authLoading || profileLoading;

  // Check if user is verified
  const isVerified = () => {
    return user?.mobile_verified === true;
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return user?.roles?.includes(role) || false;
  };

  // Fetch user profile if authenticated but no user data
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (token && !user && !profileLoading) {
      dispatch(getUserProfile());
    }
  }, [dispatch, user, profileLoading]);

  // Set checking to false when loading is done
  useEffect(() => {
    if (!loading) {
      setChecking(false);
    }
  }, [loading]);

  // Handle auth errors
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Show loading spinner while checking auth
  if (loading || checking) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  // Check if user is actually authenticated (token exists)
  const isAuth = isAuthenticated();

  // Not logged in - redirect to login
  if (!isAuth && !user) {
    toast.error("Please login to access this page");
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role requirement
  if (requiredRole && !hasRole(requiredRole)) {
    toast.error("You do not have permission to access this page");
    return <Navigate to="/" replace />;
  }

  // Check verification requirement
  if (requiredVerification && !isVerified()) {
    toast.error("Please verify your mobile number to access this page");
    return <Navigate to="/verify" replace />;
  }

  // Wrap with Layout if children is not already wrapped
  return <Layout>{children}</Layout>;
};

// HOC to wrap components with UserAuth
export const withAuth = (Component, options = {}) => {
  return function AuthenticatedComponent(props) {
    return (
      <UserAuth {...options}>
        <Component {...props} />
      </UserAuth>
    );
  };
};

// Guard for routes array
export const protectRoute = (element, options = {}) => {
  return <UserAuth {...options}>{element}</UserAuth>;
};

export default UserAuth;
